import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react'
import {
  Clipboard,
  ClipboardPaste,
  ImageUp,
  Library,
  ListFilter,
  Loader2,
  Metronome,
  Music,
  Pencil,
  Save,
  Sparkles,
  Trash2,
  Unlink,
  X
} from 'lucide-react'
import {
  formatBytes,
  resolveMediaUrl,
  type AudioGroup,
  type AudioTrack
} from '../../../api'
import type {
  AudioTrackLabelSaveInput,
  Label
} from '../../../api/labels'
import { Button, ChipSelect, IconButton, MobileEditorSheet, StatusBadge } from '../../../components/ui'
import { labelUiStyles } from '../../labels/labelUiStyles'
import type { AudioTrackMetadataItem } from '../formatAudioMetadata'
import { formatBpmValue, metadataIcons, trackEditorHeaderMeta } from './AudioTrackDialog.helpers'
import styles from './AudioTrackDialog.module.css'

type MobileEditorTab = 'info' | 'flac' | 'bindings'

type AudioTrackMobileSheetProps = {
  artist: string
  bpmHelperText: string
  canCheckPromptLibrary: boolean
  checkingPromptLibrary: boolean
  currentStylePrompt: string
  deletingLossless: boolean
  groupIds: string[]
  groups: AudioGroup[]
  imageUrl: string | null
  isEditingLinkedPrompt: boolean
  labelClipboardMessage: string | null
  losslessFileName: string | null
  losslessStatusLabel: string
  losslessUploadError: string | null
  manualBpmInput: string
  manualBpmInvalid: boolean
  metadataItems: AudioTrackMetadataItem[]
  mobileEditorTab: MobileEditorTab
  mobileImageInputRef: RefObject<HTMLInputElement | null>
  mobileLosslessInputRef: RefObject<HTMLInputElement | null>
  onClose: () => void
  promptLibraryMessage: string | null
  saving: boolean
  selectedLabels: Label[]
  setGroupIds: Dispatch<SetStateAction<string[]>>
  setLabelAssignments: Dispatch<SetStateAction<AudioTrackLabelSaveInput[]>>
  setLabelClipboardMessage: Dispatch<SetStateAction<string | null>>
  setLosslessDeleteDialogOpen: Dispatch<SetStateAction<boolean>>
  setManualBpmInput: Dispatch<SetStateAction<string>>
  setManualBpmSource: Dispatch<SetStateAction<'number' | 'tap' | null>>
  setMobileEditorTab: Dispatch<SetStateAction<MobileEditorTab>>
  setPromptLibraryMessage: Dispatch<SetStateAction<string | null>>
  setStylePrompt: Dispatch<SetStateAction<string>>
  setSuggestionsDialogOpen: Dispatch<SetStateAction<boolean>>
  setTitle: Dispatch<SetStateAction<string>>
  setArtist: Dispatch<SetStateAction<string>>
  stylePrompt: string
  stylePromptRef: RefObject<HTMLTextAreaElement | null>
  suggestionCount: number
  tapButtonLabel: string
  tapPulseKey: number
  title: string
  track: AudioTrack
  trackWithImage: AudioTrack
  unlinking: boolean
  updatingImage: boolean
  uploadingLossless: boolean
  checkPromptLibrary: () => Promise<void>
  copySelectedLabels: () => Promise<void>
  deleteImage: () => Promise<void>
  enterPromptEditMode: () => void
  handleTapBpmBeat: () => void
  openLabelBrowser: () => void
  openSavePromptDialog: () => void
  pasteLabels: () => Promise<void>
  resizeStylePromptInput: () => void
  saveTrack: () => Promise<void>
  unlinkPrompt: () => Promise<void>
  uploadImage: (event: ChangeEvent<HTMLInputElement>) => Promise<void>
  uploadLosslessSource: (event: ChangeEvent<HTMLInputElement>) => Promise<void>
}

const panelClassName = (active: boolean): string =>
  [styles.mobileSection, styles.mobileTabPanel, active ? styles.mobileTabPanelActive : ''].filter(Boolean).join(' ')

const mobileTabs: Array<{ id: MobileEditorTab; label: string }> = [
  { id: 'info', label: 'Info' },
  { id: 'flac', label: 'FLAC' },
  { id: 'bindings', label: 'Groups & labels' }
]

export const AudioTrackMobileSheet = ({
  artist,
  bpmHelperText,
  canCheckPromptLibrary,
  checkingPromptLibrary,
  checkPromptLibrary,
  copySelectedLabels,
  currentStylePrompt,
  deleteImage,
  deletingLossless,
  enterPromptEditMode,
  groupIds,
  groups,
  handleTapBpmBeat,
  imageUrl,
  isEditingLinkedPrompt,
  labelClipboardMessage,
  losslessFileName,
  losslessStatusLabel,
  losslessUploadError,
  manualBpmInput,
  manualBpmInvalid,
  metadataItems,
  mobileEditorTab,
  mobileImageInputRef,
  mobileLosslessInputRef,
  onClose,
  openLabelBrowser,
  openSavePromptDialog,
  pasteLabels,
  promptLibraryMessage,
  resizeStylePromptInput,
  saveTrack,
  saving,
  selectedLabels,
  setArtist,
  setGroupIds,
  setLabelAssignments,
  setLabelClipboardMessage,
  setLosslessDeleteDialogOpen,
  setManualBpmInput,
  setManualBpmSource,
  setMobileEditorTab,
  setPromptLibraryMessage,
  setStylePrompt,
  setSuggestionsDialogOpen,
  setTitle,
  stylePrompt,
  stylePromptRef,
  suggestionCount,
  tapButtonLabel,
  tapPulseKey,
  title,
  track,
  trackWithImage,
  unlinking,
  unlinkPrompt,
  updatingImage,
  uploadImage,
  uploadingLossless,
  uploadLosslessSource
}: AudioTrackMobileSheetProps) => {
  const toggleGroup = (groupId: string): void => {
    setGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }

  return (
    <MobileEditorSheet
      activeTab={mobileEditorTab}
      actions={(
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button disabled={saving || manualBpmInvalid} onClick={() => void saveTrack()} variant="primary">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </>
      )}
      ariaLabel="Edit track"
      closeButton={(
        <button
          aria-label="Close track editor"
          className="modal-close-button"
          onClick={onClose}
          type="button"
        >
          <X size={18} />
        </button>
      )}
      kicker="Edit track"
      onTabChange={setMobileEditorTab}
      subtitle={trackEditorHeaderMeta(track) ?? 'Metadata, groups, and labels'}
      tabs={mobileTabs}
      title={title || track.title}
    >
      <section className={panelClassName(mobileEditorTab === 'info')}>
        <div className={styles.mobileArtworkRow}>
          <button
            className={imageUrl ? styles.mobileArtworkPreview : `${styles.mobileArtworkPreview} ${styles.mobileArtworkEmpty}`}
            onClick={() => mobileImageInputRef.current?.click()}
            type="button"
          >
            {imageUrl ? (
              <img alt="" src={resolveMediaUrl(imageUrl)} />
            ) : (
              <Music size={22} />
            )}
          </button>
          <div className={styles.mobileArtworkActions}>
            <StatusBadge className={styles.mobileStatusBadge} label={track.status} />
            <Button
              disabled={updatingImage}
              icon={updatingImage ? <Loader2 size={16} /> : <ImageUp size={16} />}
              onClick={() => mobileImageInputRef.current?.click()}
            >
              {imageUrl ? 'Replace image' : 'Upload image'}
            </Button>
            {imageUrl && (
              <Button
                disabled={updatingImage}
                icon={<Trash2 size={16} />}
                onClick={() => void deleteImage()}
              >
                Remove
              </Button>
            )}
          </div>
          <input
            accept="image/*"
            hidden
            onChange={(event) => void uploadImage(event)}
            ref={mobileImageInputRef}
            type="file"
          />
        </div>

        <div className={styles.mobileFieldGrid}>
          <label className="form-group">
            <span className="form-label">Title</span>
            <input className="form-input" onChange={(event) => setTitle(event.target.value)} value={title} />
          </label>
          <label className="form-group">
            <span className="form-label">Artist</span>
            <input className="form-input" onChange={(event) => setArtist(event.target.value)} placeholder="Unknown artist" value={artist} />
          </label>
        </div>

        <div className="form-group">
          <span className="form-label">BPM</span>
          <div className={styles.mobileBpmRow}>
            <input
              className="form-input"
              inputMode="decimal"
              max={220}
              min={40}
              onChange={(event) => {
                setManualBpmInput(event.target.value)
                setManualBpmSource(event.target.value.trim() ? 'number' : null)
              }}
              placeholder={track.detected_bpm !== null ? track.detected_bpm.toString() : 'Set BPM'}
              type="number"
              value={manualBpmInput}
            />
            <Button className={styles.tapButton} onClick={handleTapBpmBeat} variant="primary">
              {tapPulseKey > 0 && <span aria-hidden="true" className={styles.tapPulse} key={tapPulseKey} />}
              <Metronome size={16} /> {tapButtonLabel}
            </Button>
          </div>
          <span className="form-label-hint">{bpmHelperText}</span>
          {manualBpmInvalid && <span className="form-error">BPM must be from 40 to 220.</span>}
        </div>

        <div className={styles.mobileMetaStrip} aria-label="File metadata">
          {metadataItems.map((item) => {
            const Icon = metadataIcons[item.key]

            return (
              <span key={item.key} title={item.label}>
                <Icon size={14} />
                {item.value}
              </span>
            )
          })}
          {track.detected_bpm !== null && (
            <span title="Auto BPM">
              <Metronome size={14} />
              Auto BPM {formatBpmValue(track.detected_bpm)}
            </span>
          )}
        </div>
      </section>

      <section className={panelClassName(mobileEditorTab === 'flac')}>
        <div className={styles.mobileSectionHeader}>
          <div>
            <span className="form-label">Lossless source</span>
            <strong>{losslessStatusLabel}</strong>
          </div>
          {trackWithImage.lossless_status !== 'missing' && (
            <IconButton
              aria-label="Remove FLAC from track"
              disabled={uploadingLossless || deletingLossless}
              icon={deletingLossless ? <Loader2 size={15} /> : <Trash2 size={15} />}
              label="Remove FLAC from track"
              onClick={() => setLosslessDeleteDialogOpen(true)}
              size="sm"
            />
          )}
        </div>
        <p className={styles.mobileHint}>
          {trackWithImage.lossless_status === 'ready'
            ? `${losslessFileName ?? 'FLAC source'}${trackWithImage.lossless_size_bytes !== null ? ` · ${formatBytes(trackWithImage.lossless_size_bytes)}` : ''}${trackWithImage.lossless_loudness_lufs !== null ? ` · ${trackWithImage.lossless_loudness_lufs.toFixed(1)} LUFS` : ''}`
            : trackWithImage.lossless_status === 'processing'
              ? 'Preserved source is being prepared.'
              : trackWithImage.lossless_status === 'failed' && trackWithImage.lossless_error
                ? trackWithImage.lossless_error
                : 'Upload a WAV or FLAC source for lossless playback.'}
        </p>
        {losslessUploadError && <span className="form-error">{losslessUploadError}</span>}
        <Button
          disabled={uploadingLossless || deletingLossless}
          icon={uploadingLossless ? <Loader2 size={16} /> : <Sparkles size={16} />}
          onClick={() => mobileLosslessInputRef.current?.click()}
        >
          {uploadingLossless ? 'Uploading...' : 'Upload WAV/FLAC'}
        </Button>
        <input
          accept=".wav,.flac,audio/wav,audio/x-wav,audio/flac,audio/x-flac"
          hidden
          onChange={(event) => void uploadLosslessSource(event)}
          ref={mobileLosslessInputRef}
          type="file"
        />
      </section>

      <section className={panelClassName(mobileEditorTab === 'info')}>
        {trackWithImage.suno_prompt_binding && !isEditingLinkedPrompt ? (
          <div className={styles.promptBinding}>
            <div className={styles.promptBindingHeader}>
              <div className={styles.promptBindingTitle}>
                <span className="form-label">Linked library prompt</span>
                <strong>
                  {trackWithImage.suno_prompt_binding.current_prompt_title ?? trackWithImage.suno_prompt_binding.prompt_snapshot.title}
                </strong>
              </div>
              <div className={styles.mobilePromptActions}>
                <IconButton
                  className={styles.mobilePromptIconButton}
                  icon={<Pencil size={16} />}
                  label="Edit linked prompt"
                  onClick={enterPromptEditMode}
                  size="sm"
                />
                <IconButton
                  className={styles.mobilePromptIconButton}
                  disabled={unlinking}
                  icon={unlinking ? <Loader2 className="tools-spin" size={16} /> : <Unlink size={16} />}
                  label="Unlink prompt"
                  onClick={() => void unlinkPrompt()}
                  size="sm"
                />
              </div>
            </div>
            <div className={styles.promptBindingPreview}>
              {trackWithImage.suno_prompt_binding.prompt_snapshot.description && (
                <small>{trackWithImage.suno_prompt_binding.prompt_snapshot.description}</small>
              )}
              <small className={styles.promptBindingText}>{trackWithImage.suno_prompt_binding.prompt_snapshot.style_prompt}</small>
            </div>
          </div>
        ) : (
          <label className="form-group">
            <span className={styles.mobilePromptHeader}>
              <span className="form-label">Suno style prompt</span>
              <span>
                <Button disabled={!currentStylePrompt} icon={<Save size={16} />} onClick={openSavePromptDialog}>
                  Save
                </Button>
                <Button
                  disabled={!canCheckPromptLibrary}
                  icon={checkingPromptLibrary ? <Loader2 className="tools-spin" size={16} /> : <Library size={16} />}
                  onClick={() => void checkPromptLibrary()}
                >
                  Check
                </Button>
              </span>
            </span>
            <textarea
              className={`form-input ${styles.promptInput}`}
              onChange={(event) => {
                setStylePrompt(event.target.value)
                setPromptLibraryMessage(null)
                resizeStylePromptInput()
              }}
              placeholder="Paste the original Suno style prompt"
              ref={stylePromptRef}
              rows={3}
              value={stylePrompt}
            />
            {promptLibraryMessage && <span className="form-label-hint">{promptLibraryMessage}</span>}
          </label>
        )}
      </section>

      <section className={panelClassName(mobileEditorTab === 'bindings')}>
        <span className="form-label">Groups</span>
        <ChipSelect
          empty={<span className="muted">No audio groups created.</span>}
          onToggle={toggleGroup}
          options={groups.map((group) => ({ id: group.id, label: group.name }))}
          selectedIds={groupIds}
        />
      </section>

      <section className={panelClassName(mobileEditorTab === 'bindings')}>
        <div className={styles.mobileSectionHeader}>
          <div>
            <span className="form-label">Labels</span>
            <strong>{selectedLabels.length} selected</strong>
          </div>
          <div className={styles.mobileInlineActions}>
            <Button icon={<ListFilter size={16} />} onClick={openLabelBrowser} variant="primary">
              Add
            </Button>
            <Button icon={<Sparkles size={16} />} onClick={() => setSuggestionsDialogOpen(true)}>
              Suggestions{suggestionCount > 0 ? ` ${suggestionCount}` : ''}
            </Button>
          </div>
        </div>
        {selectedLabels.length > 0 ? (
          <div className={labelUiStyles.selectedLabels}>
            {selectedLabels.map((label) => (
              <button
                className={labelUiStyles.selectedLabel}
                key={label.id}
                onClick={() => setLabelAssignments((current) => current.filter((item) => item.labelId !== label.id))}
                title={`Remove ${label.name}`}
                type="button"
              >
                <span>{label.name}</span>
                <X size={13} />
              </button>
            ))}
          </div>
        ) : (
          <p className={styles.mobileHint}>No labels selected. Add labels or paste labels copied from another track.</p>
        )}
        <div className={styles.mobileInlineActions}>
          <Button disabled={selectedLabels.length === 0} icon={<Clipboard size={15} />} onClick={() => void copySelectedLabels()}>
            Copy
          </Button>
          <Button icon={<ClipboardPaste size={15} />} onClick={() => void pasteLabels()}>
            Paste
          </Button>
          <Button
            disabled={selectedLabels.length === 0}
            icon={<Trash2 size={15} />}
            onClick={() => {
              setLabelAssignments([])
              setLabelClipboardMessage(null)
            }}
          >
            Clear
          </Button>
        </div>
        {labelClipboardMessage && <span className={styles.labelUtilityMessage}>{labelClipboardMessage}</span>}
      </section>
    </MobileEditorSheet>
  )
}
